import type { CSSObject } from '@emotion/react'
import {
  Color,
  FontWeight,
  gemVar,
  LineWeight,
  Size,
  Spacing,
  Theme,
  ThemedColors,
  Typography,
} from '@opallabs/gem'

type StyleStates = {
  theme: Theme
  /** The caller's `theme` prop, which pins the token to that palette. */
  providedTheme: Theme | undefined
  searchable: boolean
  interactive: boolean
  inputFocused: boolean
  /** Something sits in front of the text: the plus, or a caller's node. */
  leading: boolean
  removable: boolean
  placeholder: boolean
}

/**
 * A token takes its colours from the theme round it, unless a caller names a
 * theme: In-Market Preview sets dark tokens in a light `ThemeProvider`. A named
 * theme gets the fill and text the token had before the restyle.
 */
const palette = (providedTheme: Theme | undefined) =>
  providedTheme === undefined
    ? {
        fill: gemVar(ThemedColors.Surface2),
        stroke: gemVar(ThemedColors.StrokeSecondary2),
        text: gemVar(ThemedColors.TextPrimary),
        quietText: gemVar(ThemedColors.TextSecondary),
      }
    : {
        fill: Color[providedTheme].Surface.N300,
        stroke: Color[providedTheme]['Strokes/Secondary-2'],
        text: Color[providedTheme].Text.Default,
        quietText: Color[providedTheme]['Text/Secondary'],
      }

/**
 * The token of the Web Components Figma file: a 24px pill on Surface 2 with a
 * hairline that darkens on hover. The text is inset 8px, and 4px on any side
 * that holds a mark, so a mark sits as close to the edge as the text does.
 */
export const base = ({
  theme,
  providedTheme,
  searchable,
  removable,
  interactive,
  leading,
  inputFocused,
}: StyleStates): CSSObject => ({
  ...Typography.T300,
  // The design sets token text in Medium; Gem's 12px style is Semibold.
  fontWeight: FontWeight.Medium,
  alignItems: 'center',
  backgroundColor: searchable
    ? Color[theme].Translucent.A12
    : palette(providedTheme).fill,
  blockSize: Spacing.L,
  border: `${LineWeight.Small}px solid ${
    inputFocused ? Color[theme].Surface.A200 : palette(providedTheme).stroke
  }`,
  borderRadius: Size.Rounding,
  boxSizing: 'border-box',
  color: palette(providedTheme).text,
  columnGap: Spacing.XS,
  display: 'flex',
  flexFlow: 'row nowrap',
  paddingInlineEnd: removable ? Spacing.XS : Spacing.S,
  paddingInlineStart: leading ? Spacing.XS : Spacing.S,
  width: 'fit-content',

  '&:hover': {
    // Stroke Secondary 3 is the same grey in both themes.
    borderColor: gemVar(ThemedColors.StrokeSecondary3),
    cursor: interactive ? 'pointer' : 'auto',
  },
  '&:focus-within, &:active': {
    borderColor: Color[theme].Surface.A200,
  },
})

export const iconColor = (theme: Theme, searchable: boolean): CSSObject => ({
  color: searchable ? Color[theme].Text.Default : Color[theme].Surface.N600,
  fill: searchable ? Color[theme].Text.Default : Color[theme].Surface.N600,
})

export const iconPosition: CSSObject = {
  position: 'relative',
}

/** The quiet word in front of the text: the set a label came from, "Start". */
export const leadingText = (providedTheme: Theme | undefined): CSSObject => ({
  color: palette(providedTheme).quietText,
  whiteSpace: 'nowrap',
})

/** Only the text gives way: a long value is clipped, the words round it stay. */
export const text: CSSObject = {
  minInlineSize: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

/** The quiet phrase after the text: "except 2 accounts". */
export const trailingText = (providedTheme: Theme | undefined): CSSObject => ({
  color: palette(providedTheme).quietText,
  whiteSpace: 'nowrap',
})

/** A 16px box for the 16px close mark, with nothing round it. */
export const buttonOverrides: CSSObject = {
  blockSize: Size.Icon,
  inlineSize: Size.Icon,
  minInlineSize: 0,
  padding: 0,
}

export const input = (theme: Theme, searchable: boolean): CSSObject => ({
  ...Typography.T500,
  border: 'none',
  fontSize: '13px',
  lineHeight: '16px',
  outline: 'none',
  width: searchable ? 93 : 30,
  cursor: searchable ? 'text' : 'inherit',
  backgroundColor: 'transparent',
  color: Color[theme].Text.Default,

  '&::placeholder': {
    opacity: !searchable ? 1 : 'auto',
    color: Color[theme].Text.Default,
  },
})
